// Modules
import React from "react";

// Styles
import "./AdvancedSearch.sass";
// @ts-ignore
import { LocalizationContext } from 'react-localize';

// Interfaces
import ContractType from "Interfaces/AdvanceSearchInterface"
import FormData from "Interfaces/FormDataInterface";
import ContractToolTip from "Interfaces/ContractToolTipInterface";

// Components
import MvCheckBox from "Generic/MvCheckBox";
import LocalizationService from "../../../../Services/localization.service";
import ToolTip, { Position } from "Generic/ToolTip/ToolTip";
import { Tip } from "Interfaces/TipInterface";

const lodash = require("lodash");

type AdvancedSearchState = {
  containerHeight: number,
  showMore: boolean,
  transitioning: boolean,
  showMinContractErr: boolean;
};

export interface Props {
  show:boolean;
  selected: any;
  onChange: (e: any) => void;
  contracts: ContractType[];
  onClearSearch: (e: any) => void;
  onSubmit: (e: any) => void;
  validForm: boolean;
  onAdvancedSearch: any;
  form: FormData,
  localize: Function;
  enableOwnerSearchAvailability: boolean;
  contractToolTips: ContractToolTip[];
};

class AdvancedSearch extends React.Component<Props, AdvancedSearchState> {
  localizationService: LocalizationService;
  advancedSearchContainer:React.RefObject<HTMLDivElement>;
  advancedSearchWrapper: React.RefObject<HTMLDivElement>;
  width:number = 3;
  labelStyle = {
    color: "#303D86",
    textDecoration: "underline"
  };

  constructor(props:Props) {
    super(props);
    this.localizationService = new LocalizationService();

    this.selectItem = this.selectItem.bind(this);
    this.advancedSearchContainer = React.createRef();
    this.advancedSearchWrapper = React.createRef();
    this.resize = this.resize.bind(this);
    this.selectAll = this.selectAll.bind(this);
    this.state = {
      showMore: false,
      containerHeight: 0,
      transitioning: true,
      showMinContractErr: false,
    };
  }

  getContainerHeight() {
    let containerHeight = 0;
    if (this.advancedSearchContainer.current) {
      containerHeight = this.advancedSearchContainer.current.clientHeight;
    }
    return containerHeight;
  }

  resize() {
    let containerHeight = this.getContainerHeight();
    if (this.state.containerHeight !== containerHeight) {
      this.setState({ containerHeight: containerHeight });
    }
  }

  componentDidMount() {
    this.setState({ containerHeight: this.getContainerHeight() });
    window.addEventListener("resize", this.resize);
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.resize);
  }

  componentDidUpdate() {
    this.resize();
  }

  selectItem(name: string) {
    let selectedContract = name;
    let selected = JSON.parse(JSON.stringify(this.props.selected)); //create a deep copy instead of a shallow copy
    // let selected = this.props.selected;
    let inx = this.props.selected.indexOf(selectedContract);
    if (inx > -1) {
      //don't allow deselect if only one is selected
      if(this.props.selected.length === 1) {
        this.setState({showMinContractErr: true});
        return false;
      }

      selected.splice(inx, 1);
    } else {
      selected.push(selectedContract);
    }

    //remove min contract err
    if (this.state.showMinContractErr) {
      this.setState({showMinContractErr: false});
    }

    let event = {
      target: {
        value: selected,
        name: "contracts"
      }
    };
    this.props.onChange(event);
  }

  /**
   * Called when the 'Select All' checkbox is clicked
   */
  selectAll(){
    // if all contracts are already selected,
    // deselect all except the first contract in the first contract type (if it exists)
    if(this.checkIfAllSelected() && this.props.contracts[0].contracts) {
      // first contract id
      const value = [this.props.contracts[0].contracts[0].id];
      let event = {
        target: {
          value,
          name: "contracts"
        }
      };
      this.props.onChange(event);
      return;
    }

    this.props.onChange({
      target: {
        value: this.props.contracts.filter((x: any) => x.isSearchAvailabilityValidContract).map((item: any) => {return item.contracts.map((contract: any) => {return contract.id})}).flat(),
        name: 'contracts'
      }
    });

    // remove min contract error message if showing
    if (this.state.showMinContractErr) {
      this.setState({showMinContractErr: false});
    }
  }

  /**
   * loop through each contract for each contract type and see if all are selected
   */
  checkIfAllSelected(): boolean {
    for (const contractType of this.props.contracts) {
      for (const contract of contractType.contracts) {
        if (this.props.selected.indexOf(contract.id) === -1) {
          return false;
        }
      }
    }

    return true;
  }

  chunkArray(array: JSX.Element[], width: number) {
    return array.reduce((acc: any[][], item: any, index: number) => {
      let loc = Math.floor(index / width);
      if (!acc[loc]) {
        acc[loc] = [];
      }
      acc[loc].push(item);
      return acc;
    }, []);
  }

  getFriendlyContractTypeName = (name: string) => {
    const friendlyName = this.localizationService.getContractTypeNameFromJahia(name);
    return friendlyName['description-in-advanced-search'];
  };

  /**
   * Gets the ContractToolTip for the specific contractId
   * If it can't be found, return the generic ToolTip
   */
  getContractToolTip(contractId: string): any {
    const { contractToolTips } = this.props;

    const contractToolTip = contractToolTips === null ? null : contractToolTips.find((ctt: ContractToolTip) => {
      return ctt.contractId === contractId;
    });

    return contractToolTip ? <div style={{ marginTop: '4px' }}>{contractToolTip.toolTip}</div> : this.getContractToolTipGeneric(contractId);
  }

  /**
   * Gets the ToolTip for the specific contractId
   */
  getContractToolTipGeneric(contractId: string): any {
    const { localize } = this.context;
    const tip: [Tip] = [{ label: `${localize("static.content.ayp-contract")}`, desc: `${contractId}` }];
    const contractIdStyle = <p key={contractId} className="contract-id mv-print-link" aria-label={contractId}>{contractId}</p>;

    return <div style={{ marginTop: '4px' }}><ToolTip useRefToPosition={false} id={`contract-generic-${contractId}`} tabIndex={-1} tip={tip} position={Position.RIGHT}>
      {contractIdStyle}
    </ToolTip></div>;
  }

  render() {
    const { localize } = this.context;
    
    let initialList = this.chunkArray(
      // Return a checkbox for each contract in each contract type
      this.props.contracts.filter(x => x.contractTypeName !== 'Enrolled').map(contractType => {
        return (
          <div key={contractType.contractTypeName} className="contract-container">
            <span className="contract-header">
              {this.getFriendlyContractTypeName(contractType.contractTypeName)}
            </span>

            {contractType.isSearchAvailabilityValidContract && 
              <div className="contract-list-master">
                <span className="contract-number-row-label">{localize('static.content.advanced-search-contract-num')}:</span>
                <ul className="contract-list">
                {contractType.contracts.map(contract => {
                  return (
                    <li
                      key={contract.id}
                      className="contract"
                    >
                      <MvCheckBox
                        labelStyle={this.labelStyle}
                        onChange={() => {this.selectItem(contract.id)}}
                        checked={(this.props.selected && contractType.isSearchAvailabilityValidContract) ? (this.props.selected.indexOf(contract.id) > -1) : false}
                        label={this.getContractToolTip(contract.contractNumber || '')}
                        name={contract.id}
                        dataTestId={`advanced-search-chkbox-${contract.id}`}
                        id={`advanced-search-chkbox-${contract.id}`}
                        tabIndex={this.props.show ? 6 : -1 }
                        disabled={!contractType.isSearchAvailabilityValidContract}
                      />                    
                    </li>
                  )
                })}
                </ul>
              </div>}
            {!contractType.isSearchAvailabilityValidContract && <div style={{ fontStyle: 'italic' }}>{localize('static.content.tip.contracts-invalid')}</div>}
          </div>

        );
      }),
      this.width
    );

    let list;
    //if state is true for showMore
    if (this.state.showMore) {
      list = initialList.map((item: React.ReactNode, i) => {
        return <React.Fragment key={i}>{item}</React.Fragment>;
      });
    } else {
      // Brec: commented out because 'showMore' never gets set to true and was causing
      // unexpected behaviour with fake data. There doesn't seem to be any 'show more'
      // functionality currently, so we should show the user all available contracts.
      //list = [initialList[0]].map((item: React.ReactNode, i) => {
      list = [initialList].map((item: React.ReactNode, i) => {
        return <React.Fragment key={++i}>{item}</React.Fragment>;
      });
    }

    return (
      <div className={"twelve columns advanced-search " + (this.props.show ? "show" : "")}>
        <div className="advanced-search-wrapper" ref={this.advancedSearchWrapper} style={{ height: this.props.show ? this.state.containerHeight : 0 }}>
          <div className="advanced-search-content" ref={this.advancedSearchContainer}>
            <div className="advanced-search-body">
              <div className="advanced-search-title">
                <p className="adv-instructions">{localize('static.content.advanced-search-title')}:</p>         
                <div className="selectAll">
                  <MvCheckBox
                    onChange={this.selectAll}
                    label={localize('static.content.select-all-contracts')}
                    checked={this.checkIfAllSelected()}
                    dataTestId="advanced-search-chkbox-selectAll"
                    id="advanced-search-chkbox-selectAll"
                    tabIndex={this.props.show ? 6 : -1}
                  />
                </div>
              </div>
              <div
                className="min-contract-err-container"
                aria-live="polite"
              >
                {this.state.showMinContractErr &&
                  <p data-testid="min-contract-err">{this.props.localize("static.content.tip.min-contracts-selected")}</p>
                }
              </div>
              <div className="advanced-search-list">
                {list}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

AdvancedSearch.contextType = LocalizationContext;
export default AdvancedSearch;
